import type { Ref } from 'vue';
import { onUnmounted, ref } from 'vue';
import type { IOutright } from '@/interface/IOutright';
import { SportType } from '@/interface/enum';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import { formatOutright } from '@/libraries/oddsApi/oddsApiFormatter';
import { useOutrightsSubscription } from '@/types';

export function useOutrightInfoSubscriptionCallback({
    sportType,
    leagueId,
    oddsId,
    isLive,
    isActive = ref(true),
}: {
    sportType: SportType;
    leagueId: number;
    oddsId: number;
    isLive: boolean;
    isActive?: Ref<boolean>;
}) {
    const variables = deepCompareComputed(() => ({
        query: {
            sport: SportType.toOddsApi(sportType),
            tournamentId: leagueId,
            isLive,
        },
    }));

    const { onSubscription } = useOutrightsSubscription(
        variables,
        deepCompareComputed(() => ({
            enabled: isActive.value,
        })),
    );

    type UpdatedFn = (outrightOdds: IOutright) => void;
    let updatedFn: UpdatedFn[] = [];
    const onUpdated = (callback: UpdatedFn) => {
        updatedFn.push(callback);
        onUnmounted(() => {
            updatedFn = updatedFn.filter(fn => fn !== callback);
        });
    };

    type DeletedFn = (oddsId: number) => void;
    let deletedFn: DeletedFn[] = [];
    const onDeleted = (callback: DeletedFn) => {
        deletedFn.push(callback);
        onUnmounted(() => {
            deletedFn = deletedFn.filter(fn => fn !== callback);
        });
    };

    onSubscription((subscription) => {
        subscription?.updated.forEach((toUpdate) => {
            if (toUpdate.id === oddsId) {
                const updatedOdds = Object.freeze(formatOutright(toUpdate));
                updatedFn.forEach(fn => fn(updatedOdds));
            }
        });

        subscription?.deleted.forEach((toDeleteId) => {
            if (toDeleteId === oddsId) {
                deletedFn.forEach(fn => fn(toDeleteId));
            }
        });
    });

    return {
        onUpdated,
        onDeleted,
    };
}
